/**
 * River (§7.1, view 5): the longitudinal profile from the tailrace to the downstream gauge, gauge
 * stage against flood stages, the ramp-rate compliance gauge, and warning issuance with its
 * lead-time credit.
 */

import { useSession } from '../../store/session';
import { useDispatch } from '../useEngine';
import { getSite } from '../../sites';
import { flowRateForStageRate } from '../../engine/tailwater';
import { Trend } from '../charts/Trend';
import { Readout } from '../widgets/Readout';
import { Gloss } from '../glossary/GlossaryDrawer';
import { duration } from '../format';

export function RiverView(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const dispatch = useDispatch();
  if (!snap) return <div style={{ padding: 24 }}>—</div>;

  const ds = site.downstream;
  const lic = site.licence;
  const ramp = snap.rampMPerH;
  const downBreach = ramp < -lic.downRampMPerH;
  const upBreach = ramp > lic.upRampMPerH;
  const allowedDown = flowRateForStageRate(site, snap.totalOutflow, lic.downRampMPerH);
  const allowedUp = flowRateForStageRate(site, snap.totalOutflow, lic.upRampMPerH);
  const warnedFor = snap.floodWarningAt !== null ? snap.simMinute - snap.floodWarningAt : null;
  const credited = warnedFor !== null && warnedFor >= 360;

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gap: 'var(--s3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 'var(--s2)' }}>
        <Readout
          label={`${ds.gaugeName} flow`}
          value={Math.round(snap.gaugeFlow).toLocaleString('en-US')}
          unit="m³/s"
          tone={
            snap.gaugeFlow >= ds.floodMajor
              ? 'alarm'
              : snap.gaugeFlow >= ds.floodModerate
                ? 'alarm'
                : snap.gaugeFlow >= ds.floodMinor
                  ? 'warn'
                  : 'normal'
          }
        />
        <Readout
          label="Total release"
          value={Math.round(snap.totalOutflow).toLocaleString('en-US')}
          unit="m³/s"
          size="sm"
          tone={snap.totalOutflow < snap.minimumFlow ? 'alarm' : 'normal'}
          hint={`Licence minimum ${snap.minimumFlow} m³/s`}
        />
        <Readout
          label="Ramp rate"
          value={ramp.toFixed(3)}
          unit="m/h"
          size="sm"
          tone={downBreach || upBreach ? 'alarm' : 'normal'}
          hint={`Limits −${lic.downRampMPerH} / +${lic.upRampMPerH} m/h of tailwater stage`}
        />
        <Readout
          label="Allowed change"
          value={`−${Math.round(allowedDown)} / +${Math.round(allowedUp)}`}
          unit="m³/s per h"
          size="sm"
        />
        <Readout
          label="Flood warning"
          value={warnedFor === null ? 'not issued' : duration(warnedFor)}
          size="sm"
          tone={credited ? 'ok' : warnedFor !== null ? 'warn' : 'dim'}
          hint="Issued at least six hours before a breach reduces damages 40 %."
        />
      </div>

      <section className="panel">
        <div className="label" style={{ marginBottom: 8 }}>
          Longitudinal profile — tailrace to {ds.gaugeName} ({ds.distanceKm} km)
        </div>
        <LongProfile site={site} snap={snap} />
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 'var(--s3)' }}>
        <section className="panel">
          <div className="label" style={{ marginBottom: 6 }}>
            Gauge flow against flood stages
          </div>
          <Trend
            height={180}
            yLabel="m³/s"
            fmt={(v) => Math.round(v).toLocaleString('en-US')}
            series={[
              { x: snap.history.minuteT, y: snap.history.gauge, colour: 'var(--water-shallow)', width: 2, label: ds.gaugeName },
              { x: snap.history.minuteT, y: snap.history.outflow, colour: 'var(--ink-dim)', width: 1.2, label: 'dam release' },
            ]}
            thresholds={[
              { value: ds.floodMinor, colour: 'var(--warn)', label: 'minor' },
              { value: ds.floodModerate, colour: 'var(--alarm)', label: 'moderate' },
              { value: ds.floodMajor, colour: 'var(--alarm)', label: 'major' },
            ]}
          />
          <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 4 }}>
            Your release reaches the gauge about {site.downstream.muskingumK.toFixed(1)} hours later
            and arrives lower and wider than it left — <Gloss id="muskingum">Muskingum routing</Gloss>.
            {ds.localInflowName} joins below the dam, so nothing you release offsets it.
          </div>
        </section>

        <section className="panel">
          <div className="label" style={{ marginBottom: 6 }}>
            <Gloss id="ramping">Ramping</Gloss> compliance
          </div>
          <RampGauge ramp={ramp} down={lic.downRampMPerH} up={lic.upRampMPerH} />
          <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 10 }}>
            Limits are on tailwater <em>stage</em>, not flow. At the present release that works out
            to about <span className="num">{Math.round(allowedDown)} m³/s per hour</span> down —
            roughly a third of one unit. You cannot react; you can only anticipate.
          </div>

          <div className="label" style={{ marginTop: 14, marginBottom: 6 }}>
            Downstream actions · <Gloss id="eap">EAP</Gloss>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              className="primary"
              disabled={snap.floodWarningAt !== null}
              onClick={() => dispatch({ t: 'issue-flood-warning' })}
            >
              {snap.floodWarningAt !== null ? 'Flood warning issued' : 'Issue flood warning'}
            </button>
            {[1, 2, 3].map((lvl) => (
              <button
                key={lvl}
                className={snap.eapLevel >= lvl ? 'armed' : undefined}
                onClick={() =>
                  useSession.getState().requestConfirm({
                    label: `Declare EAP level ${lvl}`,
                    detail:
                      lvl === 3
                        ? 'Evacuation notice to the downstream community. This cannot be walked back.'
                        : lvl === 2
                          ? 'Notifies the regulator and the town EOC.'
                          : 'Opens the incident log and notifies dam safety.',
                    confirm: () => dispatch({ t: 'declare-eap', level: lvl }),
                  })
                }
              >
                EAP {lvl}
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function LongProfile({
  site,
  snap,
}: {
  site: ReturnType<typeof getSite>;
  snap: NonNullable<ReturnType<typeof useSession.getState>['snapshot']>;
}): JSX.Element {
  const W = 900;
  const H = 170;
  const ds = site.downstream;
  const g = snap.gaugeFlow;
  const stage = g >= ds.floodMajor ? 3 : g >= ds.floodModerate ? 2 : g >= ds.floodMinor ? 1 : 0;
  const colour = stage >= 2 ? 'var(--alarm)' : stage === 1 ? 'var(--warn)' : 'var(--water)';
  const depth = Math.min(60, (g / ds.floodMajor) * 50);
  const bed = H - 26;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Longitudinal river profile">
      <path d={`M 0 ${bed - 10} L ${W} ${bed + 6}`} stroke="var(--line)" strokeWidth={1} fill="none" />
      <path
        d={`M 0 ${bed - 10} L ${W} ${bed + 6} L ${W} ${bed + 6 - depth} Q ${W / 2} ${bed - 6 - depth} 0 ${bed - 10 - depth * 0.85} Z`}
        fill={colour}
        opacity={0.45}
      />
      {/* Flood-stage reference marks at the gauge, on the same depth scale. */}
      {[
        { v: ds.floodMinor, label: 'minor', c: 'var(--warn)' },
        { v: ds.floodModerate, label: 'moderate', c: 'var(--alarm)' },
        { v: ds.floodMajor, label: 'major', c: 'var(--alarm)' },
      ].map((t) => {
        const d = Math.min(60, (t.v / ds.floodMajor) * 50);
        return (
          <g key={t.label}>
            <line x1={W - 210} x2={W} y1={bed + 6 - d} y2={bed + 6 - d} stroke={t.c} strokeDasharray="3 3" opacity={0.8} />
            <text x={W - 214} y={bed + 9 - d} fontSize={10} fill={t.c} textAnchor="end" className="num">
              {t.label} {t.v.toLocaleString('en-US')}
            </text>
          </g>
        );
      })}
      <text x={6} y={18} fontSize={11} fill="var(--ink-dim)" className="num">
        tailrace {snap.tailwater.toFixed(2)} m · release {Math.round(snap.totalOutflow).toLocaleString('en-US')} m³/s
      </text>
      <text x={W - 4} y={18} fontSize={11} fill={colour} textAnchor="end" className="num" fontWeight={stage ? 700 : 400}>
        {ds.gaugeName} {Math.round(g).toLocaleString('en-US')} m³/s
      </text>
      <text x={W / 2} y={H - 4} fontSize={10} fill="var(--ink-dim)" textAnchor="middle">
        {ds.localInflowName} joins here · {Math.round(site.downstream.localInflowBase)} m³/s base
      </text>
    </svg>
  );
}

function RampGauge({ ramp, down, up }: { ramp: number; down: number; up: number }): JSX.Element {
  const span = Math.max(down, up) * 1.6;
  const pos = ((ramp + span) / (2 * span)) * 100;
  const okLo = ((-down + span) / (2 * span)) * 100;
  const okHi = ((up + span) / (2 * span)) * 100;
  const breach = ramp < -down || ramp > up;
  return (
    <div>
      <div style={{ position: 'relative', height: 26, background: 'var(--surface-2)', borderRadius: 3 }}>
        <div
          style={{
            position: 'absolute',
            left: `${okLo}%`,
            width: `${okHi - okLo}%`,
            top: 0,
            bottom: 0,
            background: 'rgba(75,158,115,0.22)',
            borderLeft: '1px solid var(--ok)',
            borderRight: '1px solid var(--ok)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `calc(${Math.max(0, Math.min(100, pos))}% - 1px)`,
            top: 0,
            bottom: 0,
            width: 2,
            background: breach ? 'var(--alarm)' : 'var(--ink)',
          }}
        />
      </div>
      <div className="num" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-dim)' }}>
        <span>−{span.toFixed(2)}</span>
        <span style={{ color: breach ? 'var(--alarm)' : 'var(--ink)' }}>
          {breach ? '▲ ' : ''}
          {ramp.toFixed(3)} m/h
        </span>
        <span>+{span.toFixed(2)}</span>
      </div>
    </div>
  );
}
